import {
  MINUTES_CONTENT_FIELDS,
  MINUTES_LIMITS,
  type MinutesContentField,
  type MinutesVersion,
} from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Textarea } from '@fruuit/ui'
import { type FormEvent, useEffect, useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'

export type MinutesEditorMode = 'create' | 'edit' | 'correct'

export const MINUTES_LABELS: Record<MinutesContentField, string> = {
  attendees: 'Participants',
  discussion: 'Discussion',
  company_response: 'Company response',
  fruuit_view: 'Fruuit assessment',
  agreed_actions: 'Agreed actions and owners',
  limitations: 'Evidence limits',
}

const HINTS: Record<MinutesContentField, string> = {
  attendees: 'Names and roles of everyone present.',
  discussion: 'What was discussed.',
  company_response: 'What the company said, in its own terms.',
  fruuit_view: 'Our interpretation, kept separate from the company response.',
  agreed_actions: 'Who does what, by when.',
  limitations: 'Uncertainty and missing evidence.',
}

export interface MinutesEditorDialogProps {
  open: boolean
  onClose: () => void
  mode: MinutesEditorMode
  /** The version being edited, or the published version being corrected. */
  version?: MinutesVersion
  onSubmit: (body: Record<string, unknown>) => Promise<unknown>
}

type Content = Record<MinutesContentField, string>

function fromVersion(v: MinutesVersion | undefined): Content {
  return {
    attendees: v?.attendees ?? '',
    discussion: v?.discussion ?? '',
    company_response: v?.company_response ?? '',
    fruuit_view: v?.fruuit_view ?? '',
    agreed_actions: v?.agreed_actions ?? '',
    limitations: v?.limitations ?? '',
  }
}

/** Write, edit or correct minutes (CAL-040..045). Limits mirror the server (GAP-UI-016). */
export function MinutesEditorDialog({
  open,
  onClose,
  mode,
  version,
  onSubmit,
}: MinutesEditorDialogProps) {
  const [content, setContent] = useState<Content>(() => fromVersion(version))
  const [changeReason, setChangeReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (open) {
      setContent(fromVersion(version))
      setChangeReason('')
      setErrors({})
      setFailure(null)
    }
  }, [open, version])

  async function submit(event: FormEvent) {
    event.preventDefault()
    const local: Record<string, string> = {}
    for (const field of MINUTES_CONTENT_FIELDS) {
      const min = MINUTES_LIMITS[field].min
      if (content[field].trim().length < min) local[field] = `Enter at least ${min} characters.`
    }
    if (mode === 'correct' && changeReason.trim().length < 8) {
      local.change_reason = 'Explain the correction in at least 8 characters.'
    }
    if (Object.keys(local).length > 0) {
      setErrors(local)
      return
    }
    setErrors({})
    setFailure(null)
    setPending(true)
    try {
      if (mode === 'edit' && version) {
        const changed = Object.fromEntries(
          MINUTES_CONTENT_FIELDS.filter((f) => content[f] !== fromVersion(version)[f]).map((f) => [
            f,
            content[f],
          ]),
        )
        await onSubmit({ revision: version.revision, ...changed })
      } else if (mode === 'correct') {
        await onSubmit({ change_reason: changeReason.trim(), ...content })
      } else {
        await onSubmit(content)
      }
      onClose()
    } catch (err) {
      if (isApiError(err)) setErrors(err.fieldErrors)
      setFailure(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  const title =
    mode === 'create'
      ? 'Add meeting minutes'
      : mode === 'edit'
        ? 'Edit draft minutes'
        : 'Correct published minutes'
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={
        mode === 'correct'
          ? `Creates version ${(version?.version ?? 0) + 1}. Version ${version?.version ?? ''} stays published until the correction is reviewed and released.`
          : 'Minutes stay internal until the case reviewer approves and publishes them.'
      }
      size="lg"
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="minutes-form" loading={pending}>
            {mode === 'correct' ? 'Create correction draft' : 'Save draft'}
          </Button>
        </>
      }
    >
      <form id="minutes-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {failure ? (
          <Callout tone="danger" role="alert">
            {failure}
          </Callout>
        ) : null}
        {mode === 'correct' ? (
          <Textarea
            label="What does this correction change?"
            required
            rows={2}
            maxLength={2000}
            value={changeReason}
            error={errors.change_reason}
            onChange={(e) => setChangeReason(e.target.value)}
          />
        ) : null}
        {MINUTES_CONTENT_FIELDS.map((field) => (
          <Textarea
            key={field}
            label={MINUTES_LABELS[field]}
            hint={HINTS[field]}
            required
            rows={field === 'attendees' ? 2 : 4}
            maxLength={MINUTES_LIMITS[field].max}
            value={content[field]}
            error={errors[field]}
            onChange={(e) => setContent((c) => ({ ...c, [field]: e.target.value }))}
          />
        ))}
      </form>
    </Dialog>
  )
}
